import { getClosestMatchingOption, getDefaultFormState, type RJSFSchema } from "@rjsf/utils";
import { jsonSchemaObjectValidator } from "src/components/common/forms/jsonSchema/jsonSchemaObjectValidator.ts";

export type JsonObject = Record<string, unknown>;

export type ParseJsonObjectResult =
	| { success: true; value: JsonObject }
	| { success: false; error: "INVALID_JSON" | "NOT_OBJECT" };

export type ValidateJsonSchemaObjectTextResult =
	| { success: true; value: JsonObject }
	| { success: false; errors: string[] };

export const jsonSchemaDefaultFormStateBehavior = {
	constAsDefaults: "skipOneOf",
} as const;

export function createInitialJsonSchemaObject(schemaValue: unknown, inputValue: unknown): JsonObject {
	const schema = toRjsfSchema(schemaValue);
	const suppliedValue = toJsonObject(inputValue);
	const initialValue = toJsonObject(
		getDefaultFormState(
			jsonSchemaObjectValidator,
			schema,
			suppliedValue,
			schema,
			false,
			jsonSchemaDefaultFormStateBehavior,
		),
	);
	return removeUnselectedOptionalLabeledChoices(
		schema,
		normalizeRequiredNullableFields(schema, initialValue),
		suppliedValue,
	);
}

function removeUnselectedOptionalLabeledChoices(
	schema: RJSFSchema,
	value: JsonObject,
	suppliedValue: JsonObject,
): JsonObject {
	const properties = toJsonObject(schema.properties);
	const required = new Set(Array.isArray(schema.required) ? schema.required : []);
	const normalized = { ...value };

	for (const [propertyName, propertySchemaValue] of Object.entries(properties)) {
		if (
			!required.has(propertyName) &&
			!Object.hasOwn(suppliedValue, propertyName) &&
			isLabeledConstantSelectionWithoutDefault(propertySchemaValue)
		) {
			delete normalized[propertyName];
			continue;
		}
		if (
			isJsonObject(propertySchemaValue) &&
			isJsonObject(normalized[propertyName]) &&
			isJsonObject(suppliedValue[propertyName])
		) {
			normalized[propertyName] = removeUnselectedOptionalLabeledChoices(
				propertySchemaValue,
				normalized[propertyName],
				suppliedValue[propertyName],
			);
		}
	}

	return normalized;
}

function isLabeledConstantSelectionWithoutDefault(schemaValue: unknown): boolean {
	if (!isJsonObject(schemaValue) || Object.hasOwn(schemaValue, "default")) return false;
	const alternatives = schemaValue.oneOf;
	return (
		Array.isArray(alternatives) &&
		alternatives.length > 0 &&
		alternatives.every(
			(alternative) =>
				isJsonObject(alternative) &&
				Object.hasOwn(alternative, "const") &&
				typeof alternative.title === "string",
		)
	);
}

export function normalizeRequiredNullableFields(schemaValue: unknown, value: JsonObject): JsonObject {
	const rootSchema = toRjsfSchema(schemaValue);
	return toJsonObject(normalizeSchemaValue(rootSchema, value, rootSchema));
}

function normalizeSchemaValue(
	schemaValue: unknown,
	value: unknown,
	rootSchema: RJSFSchema,
	seenReferences = new Set<string>(),
): unknown {
	const schema = toJsonObject(schemaValue);
	const referencedSchema = resolveLocalReference(schema, rootSchema);
	let normalized = value;
	if (referencedSchema != null && typeof schema.$ref === "string" && !seenReferences.has(schema.$ref)) {
		normalized = normalizeSchemaValue(
			referencedSchema,
			value,
			rootSchema,
			new Set(seenReferences).add(schema.$ref),
		);
	}

	if (isJsonObject(normalized)) {
		normalized = normalizeObjectFields(schema, normalized, rootSchema);
	} else if (Array.isArray(normalized) && isJsonObject(schema.items)) {
		normalized = normalized.map((item) => normalizeSchemaValue(schema.items, item, rootSchema));
	}

	if (Array.isArray(schema.allOf)) {
		for (const alternative of schema.allOf) {
			normalized = normalizeSchemaValue(alternative, normalized, rootSchema);
		}
	}
	for (const keyword of ["anyOf", "oneOf"] as const) {
		const alternatives = schema[keyword];
		if (!Array.isArray(alternatives) || !isJsonObject(normalized)) continue;
		const optionSchemas = alternatives.filter(isJsonObject) as RJSFSchema[];
		if (optionSchemas.length === 0) continue;
		const selectedOption = getClosestMatchingOption<JsonObject>(
			jsonSchemaObjectValidator,
			rootSchema,
			normalized,
			optionSchemas,
		);
		if (selectedOption >= 0) {
			normalized = normalizeSchemaValue(optionSchemas[selectedOption], normalized, rootSchema, seenReferences);
		}
	}

	return normalized;
}

function normalizeObjectFields(schema: JsonObject, value: JsonObject, rootSchema: RJSFSchema): JsonObject {
	const properties = toJsonObject(schema.properties);
	const required = Array.isArray(schema.required) ? schema.required : [];
	const normalized = { ...value };

	for (const propertyName of required) {
		if (typeof propertyName !== "string") continue;
		const propertySchema = toJsonObject(properties[propertyName]);
		if (normalized[propertyName] === undefined) {
			if (isNullableSchema(propertySchema, rootSchema)) {
				normalized[propertyName] = null;
			} else if (isBooleanSchema(propertySchema, rootSchema)) {
				normalized[propertyName] = false;
			}
		}
	}

	for (const [propertyName, propertySchemaValue] of Object.entries(properties)) {
		const propertyValue = normalized[propertyName];
		if (propertyValue !== undefined) {
			normalized[propertyName] = normalizeSchemaValue(propertySchemaValue, propertyValue, rootSchema);
		}
	}

	return normalized;
}

export function parseJsonSchemaObjectWithDefaults(value: string, schemaValue: unknown): ParseJsonObjectResult {
	const parsed = parseJsonObject(value);
	if (!parsed.success) return parsed;
	return {
		success: true,
		value: createInitialJsonSchemaObject(schemaValue, parsed.value),
	};
}

export function validateJsonSchemaObjectText(
	value: string,
	schemaValue: unknown,
): ValidateJsonSchemaObjectTextResult {
	const parsed = parseJsonObject(value);
	if (!parsed.success) return { success: false, errors: [parsed.error] };
	const schema = toRjsfSchema(schemaValue);
	const validation = jsonSchemaObjectValidator.validateFormData(parsed.value, schema);
	if (validation.errors.length > 0) {
		return { success: false, errors: validation.errors.map((error) => error.stack) };
	}
	return parsed;
}

export function parseJsonObject(value: string): ParseJsonObjectResult {
	let parsed: unknown;
	try {
		parsed = JSON.parse(value);
	} catch {
		return { success: false, error: "INVALID_JSON" };
	}
	if (!isJsonObject(parsed)) return { success: false, error: "NOT_OBJECT" };
	return { success: true, value: parsed };
}

export function toJsonObject(value: unknown): JsonObject {
	return isJsonObject(value) ? value : {};
}

export function toRjsfSchema(value: unknown): RJSFSchema {
	return normalizeRjsfSchema(isJsonObject(value) ? value : { type: "object" });
}

export function formatJsonObject(value: JsonObject): string {
	return JSON.stringify(value, null, 2);
}

function isJsonObject(value: unknown): value is JsonObject {
	return value != null && typeof value === "object" && !Array.isArray(value);
}

function isNullableSchema(schema: JsonObject, rootSchema: RJSFSchema, seenReferences = new Set<string>()): boolean {
	let allowsNull = true;
	if (typeof schema.type === "string") allowsNull &&= schema.type === "null";
	if (Array.isArray(schema.type)) allowsNull &&= schema.type.includes("null");
	if (Object.hasOwn(schema, "const")) allowsNull &&= schema.const === null;
	if (Array.isArray(schema.enum)) allowsNull &&= schema.enum.includes(null);

	if (typeof schema.$ref === "string") {
		if (seenReferences.has(schema.$ref)) return false;
		const referencedSchema = resolveLocalReference(schema, rootSchema);
		if (referencedSchema == null) return false;
		const nextSeenReferences = new Set(seenReferences).add(schema.$ref);
		allowsNull &&= isNullableSchema(referencedSchema, rootSchema, nextSeenReferences);
	}

	if (Array.isArray(schema.allOf)) {
		allowsNull &&= schema.allOf.every(
			(alternative) => isJsonObject(alternative) && isNullableSchema(alternative, rootSchema, seenReferences),
		);
	}
	if (Array.isArray(schema.anyOf)) {
		allowsNull &&= schema.anyOf.some(
			(alternative) => isJsonObject(alternative) && isNullableSchema(alternative, rootSchema, seenReferences),
		);
	}
	if (Array.isArray(schema.oneOf)) {
		allowsNull &&=
			schema.oneOf.filter(
				(alternative) => isJsonObject(alternative) && isNullableSchema(alternative, rootSchema, seenReferences),
			).length === 1;
	}
	if (isJsonObject(schema.not)) allowsNull &&= !isNullableSchema(schema.not, rootSchema, seenReferences);
	return allowsNull;
}

function isBooleanSchema(schema: JsonObject, rootSchema: RJSFSchema, seenReferences = new Set<string>()): boolean {
	if (schema.type === "boolean") return true;
	if (Array.isArray(schema.type)) {
		return schema.type.includes("boolean") && schema.type.every((type) => type === "boolean" || type === "null");
	}
	if (typeof schema.$ref === "string") {
		if (seenReferences.has(schema.$ref)) return false;
		const referencedSchema = resolveLocalReference(schema, rootSchema);
		return (
			referencedSchema != null &&
			isBooleanSchema(referencedSchema, rootSchema, new Set(seenReferences).add(schema.$ref))
		);
	}
	return false;
}

function resolveLocalReference(schema: JsonObject, rootSchema: RJSFSchema): JsonObject | undefined {
	if (typeof schema.$ref !== "string" || !schema.$ref.startsWith("#/")) return undefined;
	let resolved: unknown = rootSchema;
	for (const rawToken of schema.$ref.slice(2).split("/")) {
		if (!isJsonObject(resolved)) return undefined;
		const token = decodeReferenceToken(rawToken).replaceAll("~1", "/").replaceAll("~0", "~");
		resolved = resolved[token];
	}
	return isJsonObject(resolved) ? resolved : undefined;
}

function decodeReferenceToken(token: string): string {
	try {
		return decodeURIComponent(token);
	} catch {
		return token;
	}
}

function normalizeRjsfSchema(schema: JsonObject): RJSFSchema {
	const normalized: JsonObject = { ...schema };
	const properties = schema.properties;
	if (isJsonObject(properties)) {
		normalized.properties = Object.fromEntries(
			Object.entries(properties).map(([name, propertySchema]) => [
				name,
				isJsonObject(propertySchema) ? normalizeRjsfSchema(propertySchema) : propertySchema,
			]),
		);
	}
	for (const keyword of ["oneOf", "anyOf", "allOf"] as const) {
		const alternatives = schema[keyword];
		if (Array.isArray(alternatives)) {
			normalized[keyword] = alternatives.map((alternative) =>
				isJsonObject(alternative) ? normalizeRjsfSchema(alternative) : alternative,
			);
		}
	}
	if (isJsonObject(schema.items)) {
		normalized.items = normalizeRjsfSchema(schema.items);
	}
	for (const keyword of ["$defs", "definitions"] as const) {
		const definitions = schema[keyword];
		if (isJsonObject(definitions)) {
			normalized[keyword] = Object.fromEntries(
				Object.entries(definitions).map(([name, definition]) => [
					name,
					isJsonObject(definition) ? normalizeRjsfSchema(definition) : definition,
				]),
			);
		}
	}

	if (normalized.type === undefined) {
		const inferredType = inferConstantChoiceType(normalized.oneOf ?? normalized.anyOf);
		if (inferredType !== undefined) normalized.type = inferredType;
	}
	return normalized as RJSFSchema;
}

function inferConstantChoiceType(alternatives: unknown): "string" | "number" | "integer" | "boolean" | undefined {
	if (!Array.isArray(alternatives) || alternatives.length === 0) return undefined;
	const values = alternatives.map((alternative) =>
		isJsonObject(alternative) && Object.hasOwn(alternative, "const") ? alternative.const : undefined,
	);
	if (values.some((value) => value === undefined || value === null || typeof value === "object")) return undefined;
	const types = new Set(
		values.map((value) => (typeof value === "number" && Number.isInteger(value) ? "integer" : typeof value)),
	);
	if (types.size !== 1) return undefined;
	const [type] = types;
	return type === "string" || type === "number" || type === "integer" || type === "boolean" ? type : undefined;
}
